import { Loading } from 'components/loading'
import { ScoutLargeIcon } from 'components/icons/scout-large'
import { RasaReactComponent } from 'generic/rasaReactComponent'
import { RasaContext } from 'context'
import React from 'react'
import { Button } from 'reactstrap'
import * as GenerateTextData from 'components/generate-text/data'
import { AjaxWrapper, HttpMethod } from 'generic/ajaxWrapper'
import * as Flash from 'components/flash'
import { BaseClientEntity } from 'generic/baseClientEntity'
import { isInCutoffWindow } from 'shared/data-layer/issues'
import { NORMAL_BOOSTED_BOOST_LEVEL } from 'shared_server_client/constants'
import * as GA from 'google-analytics'
import './styles.scss'

interface Article {
  post_id: string
  title: string
  description: string
  url: string
  site_name?: string
  hosted_image_url?: string
  created?: string
}

interface State {
  activeCommunity: any
  articles: Article[]
  isLoading: boolean
  hasTopics: boolean
  topics: string[]
  error?: string
  addingArticles: Set<string>
  addingAll: boolean
  customSummary: string
}

const initialState: State = {
  activeCommunity: {},
  articles: [],
  isLoading: true,
  hasTopics: false,
  topics: [],
  addingArticles: new Set(),
  addingAll: false,
  customSummary: '',
}

export class ScoutRecommendedComponent extends RasaReactComponent<any, State> {
  public static contextType = RasaContext

  constructor(props: any) {
    super(props, 'scoutRecommended', initialState)
  }

  public componentDidMount = () => {
    this.context.user.init().then(({person, activeCommunity, role}) => {
      this.setState({
        activeCommunity,
      }, () => {
        this.initialize()
      })
    })
  }

  public render = () => {
    return (
      <div className="scout-recommended-wrapper">
        <div className="scout-recommended-header">
          <div className="scout-recommended-header-content">
            <ScoutLargeIcon svgwidth="64" svgheight="64" />
            <div className="scout-recommended-header-text">
              <h1>Scout Recommended</h1>
              <p>Content curated just for you based on your newsletter topics</p>
            </div>
          </div>
        </div>

        <div className="scout-recommended-content">
          {this.state.isLoading ? (
            <div className="scout-loading">
              <Loading size="32" />
              <p>Finding the best content for you...</p>
            </div>
          ) : this.state.hasTopics && this.renderCustomSummaryInput()}

          {this.state.isLoading ? null : !this.state.hasTopics ? (
            <div className="scout-no-topics">
              <div className="scout-no-topics-card">
                <ScoutLargeIcon svgwidth="80" svgheight="80" />
                <h2>We're getting to know you!</h2>
                <p>
                  Start sending newsletters so we can learn about your content preferences
                  and provide personalized recommendations.
                </p>
                <p className="scout-no-topics-hint">
                  Once you've sent a few newsletters, Scout will analyze your most popular
                  topics and suggest relevant articles here.
                </p>
              </div>
            </div>
          ) : this.state.error ? (
            <div className="scout-error">
              <p>{this.state.error}</p>
              <Button color="primary" onClick={this.initialize}>
                Try Again
              </Button>
            </div>
          ) : this.state.articles.length > 0 ? (
            <div className="scout-articles-section">
              <div className="scout-articles-header">
                <div className="scout-topics-display">
                  <h3>Based on your top topics:</h3>
                  <div className="scout-topics-list">
                    {this.state.topics.map((topic, index) => (
                      <span key={index} className="scout-topic-tag">
                        {topic}
                      </span>
                    ))}
                  </div>
                </div>
                <Button
                  color="primary"
                  onClick={this.addAllToUpcoming}
                  disabled={this.state.addingAll}
                  className="scout-add-all-btn"
                >
                  {this.state.addingAll ? 'Adding...' : `Add All ${this.state.articles.length} to Upcoming`}
                </Button>
              </div>

              <div className="scout-articles-grid">
                {this.state.articles.map((article, index) => this.renderArticle(article, index))}
              </div>
            </div>
          ) : (
            <div className="scout-no-articles">
              <p>No articles found for your topics. Try again later!</p>
              <Button color="primary" onClick={this.initialize}>
                Refresh
              </Button>
            </div>
          )}
        </div>
      </div>
    )
  }

  private renderCustomSummaryInput = () => {
    return (
      <div className="scout-summary-input">
        <h3>Customize Your Search</h3>
        <div className="scout-input-wrapper">
          <textarea
            className="scout-input-field"
            placeholder="Add custom instructions to refine your search... (e.g., 'Focus on recent breakthroughs in AI for healthcare, particularly diagnostic tools' or 'Find articles about sustainable energy published in the last 3 days')"
            value={this.state.customSummary}
            onChange={(e) => this.setState({ customSummary: e.target.value })}
            rows={3}
          />
          <Button
            color="primary"
            onClick={this.searchWithCustomSummary}
            disabled={!this.state.customSummary.trim() || this.state.isLoading}
            className="scout-search-btn"
          >
            Search Again
          </Button>
        </div>
        <p className="scout-input-hint">
          Add specific instructions to guide the AI in finding articles that match your needs. The search will still consider your topics above.
        </p>
      </div>
    )
  }

  private searchWithCustomSummary = () => {
    if (!this.state.customSummary.trim()) return

    this.setState({ isLoading: true, error: undefined })

    this.fetchRecommendedArticles(this.state.topics, this.state.customSummary.trim())
  }

  private renderArticle = (article: Article, index: number) => {
    const isAdding = this.state.addingArticles.has(article.post_id)

    return (
      <div className="scout-article-card" key={article.post_id || index}>
        <div className="scout-article-image">
          {article.hosted_image_url ? (
            <img src={article.hosted_image_url} alt={article.title} />
          ) : (
            <div className="scout-article-placeholder">
              <i className="fas fa-newspaper" />
            </div>
          )}
        </div>
        <div className="scout-article-content">
          <div className="scout-article-meta">
            {article.site_name && <span className="scout-article-source">{article.site_name}</span>}
            {article.created && (
              <span className="scout-article-date">
                {new Date(article.created).toLocaleDateString()}
              </span>
            )}
          </div>
          <h4 className="scout-article-title">
            <a href={article.url} target="_blank" rel="noopener noreferrer">
              {article.title || 'Untitled Article'}
            </a>
          </h4>
          <p className="scout-article-description">
            {article.description && article.description.length > 150
              ? `${article.description.substring(0, 150)}...`
              : article.description || 'No description available.'}
          </p>
          <div className="scout-article-actions">
            <Button
              color="primary"
              onClick={() => this.addToUpcoming(article.post_id)}
              disabled={isAdding || this.state.addingAll}
              className="scout-add-btn"
            >
              {isAdding ? 'Adding...' : 'Add to Upcoming'}
            </Button>
            <Button
              color="secondary"
              onClick={() => window.open(article.url, '_blank')}
              className="scout-read-btn"
            >
              Read Article
            </Button>
          </div>
        </div>
      </div>
    )
  }

  private initialize = () => {
    this.setState({ isLoading: true, error: undefined })

    GenerateTextData.getTopics(this.state.activeCommunity.communityId)
      .then((topics) => {
        if (!topics || topics.length === 0) {
          this.setState({
            isLoading: false,
            hasTopics: false,
          })
          return
        }

        this.setState({
          hasTopics: true,
          topics,
        })

        return this.fetchRecommendedArticles(topics)
      })
      .catch((error) => {
        this.setState({
          isLoading: false,
          error: 'Failed to load recommendations. Please try again.',
        })
        this.context.store.dispatch(
          Flash.showFlashError('Failed to load Scout recommendations.')
        )
      })
  }

  private fetchRecommendedArticles = (topics: string[], customSummary?: string) => {
    const url = `${AjaxWrapper.getServerUrl()}/dataset/${this.state.activeCommunity.communityId}/scoutRecommendedArticles`

    const payload: any = {
      topics: topics.join(','),
      num_articles: 20,
    }

    if (customSummary) {
      payload.summary = customSummary
    }

    return AjaxWrapper.ajax(url, HttpMethod.POST, payload)
      .then((response) => {
        if (response && response.articles) {
          this.setState({
            articles: response.articles,
            isLoading: false,
          })
        } else {
          this.setState({
            articles: [],
            isLoading: false,
          })
        }
      })
      .catch((error) => {
        this.setState({
          isLoading: false,
          error: 'Failed to fetch recommended articles.',
        })
        this.context.store.dispatch(
          Flash.showFlashError('Failed to fetch recommended articles.')
        )
      })
  }

  private addToUpcoming = (postId: string) => {
    this.context.store.dispatch(GA.sendGa4Event(this.ga4Event(GA.EventNames.ScoutAddArticle)))

    const addingArticles = new Set(this.state.addingArticles)
    addingArticles.add(postId)
    this.setState({ addingArticles })

    return this.context.entityMetadata
      .getEntityObject('communityArticle')
      .then((entity: BaseClientEntity) => {
        entity.setRecordId(this.state.activeCommunity.communityId, null)
        entity.data.post_ids = postId.toString()

        if (isInCutoffWindow(this.state.activeCommunity.nextIssue)) {
          entity.data.boost_level = NORMAL_BOOSTED_BOOST_LEVEL
          entity.data.created = this.state.activeCommunity.nextIssue.cutoff
        }

        return entity.save()
      })
      .then(() => {
        const updatedAddingArticles = new Set(this.state.addingArticles)
        updatedAddingArticles.delete(postId)
        this.setState({ addingArticles: updatedAddingArticles })

        this.context.store.dispatch(
          Flash.showFlashMessage('Article added to upcoming pool!')
        )
      })
      .catch(() => {
        const failedAddingArticles = new Set(this.state.addingArticles)
        failedAddingArticles.delete(postId)
        this.setState({ addingArticles: failedAddingArticles })

        this.context.store.dispatch(
          Flash.showFlashError('Failed to add article. Please try again.')
        )
      })
  }

  private addAllToUpcoming = () => {
    this.context.store.dispatch(GA.sendGa4Event(this.ga4Event(GA.EventNames.ScoutAddArticle)))

    this.setState({ addingAll: true })

    const postIds = this.state.articles.map((article) => article.post_id).join(',')

    return this.context.entityMetadata
      .getEntityObject('communityArticle')
      .then((entity: BaseClientEntity) => {
        entity.setRecordId(this.state.activeCommunity.communityId, null)
        entity.data.post_ids = postIds

        if (isInCutoffWindow(this.state.activeCommunity.nextIssue)) {
          entity.data.boost_level = NORMAL_BOOSTED_BOOST_LEVEL
          entity.data.created = this.state.activeCommunity.nextIssue.cutoff
        }

        return entity.save()
      })
      .then(() => {
        this.setState({ addingAll: false })

        this.context.store.dispatch(
          Flash.showFlashMessage(
            `All ${this.state.articles.length} articles added to upcoming pool!`
          )
        )
      })
      .catch(() => {
        this.setState({ addingAll: false })

        this.context.store.dispatch(
          Flash.showFlashError('Failed to add articles. Please try again.')
        )
      })
  }

  private ga4Event = (eventName: string): GA.Event4 => {
    return {
      name: eventName,
      payload: {
        source: 'scout-recommended',
      },
    }
  }
}
